import _ from 'underscore';

import {writeFragmentsToCache} from 'graphql-client/ApolloWrappers';
import {GraphQLRequestTasksRelationship} from 'requests/graphql/__generated/GraphQLRequestTasksRelationship';
import {RequestTasksRelationshipFragment} from 'requests/graphql/Request';
import {RequestInputsState} from 'requests/inputs/reduceInputs';
import {requestTasksFetchedForRequests} from 'requests/tasks/RequestTaskReduxActions';

import Syncer from '../Syncer';

export type ReduxRequestTasksRelationship = Pick<
  RequestInputsState,
  'requestTaskIdsByRequestId' | 'sampleGroupIdsByRequestTaskId'
>;

function toGraphQL({
  requestTaskIdsByRequestId,
  sampleGroupIdsByRequestTaskId,
}: ReduxRequestTasksRelationship): Array<GraphQLRequestTasksRelationship> {
  const requestIds = Object.keys(requestTaskIdsByRequestId);
  return requestIds.map((requestId) => ({
    __typename: 'RequestNode',
    id: requestId,
    tasks: requestTaskIdsByRequestId[requestId].map((taskId) => ({
      __typename: 'RequestTaskNode',
      id: taskId,
      sampleGroups: (sampleGroupIdsByRequestTaskId[taskId] || []).map((sampleGroupId) => ({
        __typename: 'SampleGroupNode',
        id: sampleGroupId,
      })),
    })),
  }));
}

export default class RequestTasksRelationshipSyncer extends Syncer<
  ReduxRequestTasksRelationship,
  GraphQLRequestTasksRelationship
> {
  readFromStore(): {current: ReduxRequestTasksRelationship; equalityRef: unknown} {
    const {requestTaskIdsByRequestId, sampleGroupIdsByRequestTaskId} =
      this.reduxState().models.requests.inputs;
    return {
      current: {requestTaskIdsByRequestId, sampleGroupIdsByRequestTaskId},
      equalityRef: {requestTaskIdsByRequestId, sampleGroupIdsByRequestTaskId},
    };
  }

  writeToApolloCache(inputs: ReduxRequestTasksRelationship): void {
    writeFragmentsToCache(RequestTasksRelationshipFragment, toGraphQL(inputs));
  }

  writeToStore(requests: Array<GraphQLRequestTasksRelationship>): void {
    const taskIdsByRequestId = _.mapObject(
      _.indexBy(requests, (request) => request.id),
      (request) => request.tasks.map((task) => task.id)
    );

    const tasks = requests.flatMap((request) => request.tasks);
    const sampleGroupIdsByRequestTaskId = _.mapObject(
      _.indexBy(tasks, (task) => task.id),
      (task) => task.sampleGroups.map((sampleGroup) => sampleGroup.id)
    );

    this.dispatch(requestTasksFetchedForRequests(taskIdsByRequestId, sampleGroupIdsByRequestTaskId));
  }
}
